import { FadeScroller } from "@components/atoms/FadeScroller"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { ShareButton } from "@components/atoms/ShareButton"
import { Slider } from "@components/atoms/Slider"
import { PlayTrailerButton } from "@components/media/TrailerOverlay"
import { Breadcrumbs } from "@components/page/Breadcrumbs"
import { Button } from "@components/ui/Button"
import { Checkbox } from "@components/ui/Checkbox"
import { Chip } from "@components/ui/Chip"
import { Drawer, useParentDrawer } from "@components/ui/Drawer"
import { useProgramBackButton } from "@features/program/useProgramBackButton"
import { trackEvent } from "@features/tracking/tracking"
import { WishlistButton } from "@features/wishlist/WishlistButton"
import { EventType } from "@generated-types"
import { splitFilmTitle } from "@utils/split-film-title"
import { cn, cx } from "@utils/tw"
import { defineBlock, EditableText, InspectorControls, useInlineEditableValue } from "eddev/blocks"
import { useHydrating, useRouter } from "eddev/routing"
import { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { useTimeout, useTimeoutFn } from "react-use"
import { Dialog } from "@components/ui/Dialog"
import { FilmImageEditor } from "../../backend/widgets/support/FilmImageEditor"
import { AssetManagerTaskLabel } from "../../backend/widgets/support/AssetManagerTaskLabel"

export const meta: BlockMeta = {
  title: "Header",
  icon: "flag",
  tags: ["film-root"],
  inserter: false,
  postMetaBlock: {
    fieldName: "meta",
    postTypes: ["film"],
  },
}

export default defineBlock("film/film-header", (props) => {
  const [itemConfig, setItemConfig] = useInlineEditableValue<Record<string, boolean>>("shown", {})
  const router = useRouter()
  const drawer = useParentDrawer()
  const ref = useRef<HTMLDivElement>(null)

  const [gallery, setGallery] = useState(props.film?.media?.gallery ?? [])
  useEffect(() => {
    setGallery(props.film?.media?.gallery ?? [])
  }, [props.film?.media?.gallery])

  const showBreadcrumbs = !drawer.isDrawer

  const film = props.film

  // Get the display title, separating moving the prefix onto a new line based on format
  const { filmTitle, filmTag } = splitFilmTitle(film?.title ?? "")

  // Get a list of meta tags to show
  const meta = useMemo(() => {
    return META_TYPES.map((item) => {
      let show = item.showDefault
      if (itemConfig[item.label] !== undefined) {
        show = itemConfig[item.label]
      }
      if (show) {
        return item.get(film)
      }
    })
      .filter(Boolean)
      .map((item, i) => (
        <Chip shape="rounded" variant="mid" key={i}>
          {item}
        </Chip>
      ))
  }, [itemConfig, film])

  /** Action buttons */
  const actions = [
    <ShareButton key="share" variant="ghost" />,
    <WishlistButton key="wishlist" film={props.film!} />,
    props.film?.eventInfo?.trailer && (
      <PlayTrailerButton key="trailer" videoId={props.film?.eventInfo?.trailer} filmUri={props.film.uri!}>
        <Button shape="branded" variant="primary" leftIcon="play">
          Trailer
        </Button>
      </PlayTrailerButton>
    ),
    props.film?.sessions?.length! > 0 && props.film?.eventInfo?.eventType !== EventType.Short && (
      <Button
        key="book"
        shape="branded"
        variant="hero"
        onClick={(e) => {
          trackEvent("Clicked Book Tickets", {}, e.currentTarget)
          router.replaceHash("#tickets")
          drawer.maximize()
        }}
      >
        <EditableText store="bookTicketsButton" as="div" defaultValue="Book Tickets" />
      </Button>
    ),
  ].filter(Boolean) as ReactNode[]

  return (
    <>
      {env.admin && (
        <InspectorControls>
          <div className="flex flex-col gap-5 mt-3">
            <div className="px-4 flex flex-col gap-1">
              {META_TYPES.map((credit) => {
                return (
                  <Checkbox
                    key={credit.label}
                    label={credit.label}
                    checked={itemConfig[credit.label] ?? credit.showDefault}
                    onCheckedChange={(checked) => {
                      if (checked === credit.showDefault) {
                        delete itemConfig[credit.label]
                      } else {
                        itemConfig[credit.label] = checked
                      }
                      setItemConfig({ ...itemConfig })
                    }}
                  />
                )
              })}
            </div>
            <div className="px-4">
              <Dialog trigger={<Button rightIcon="film">Edit Images</Button>}>
                <Dialog.Title />
                <Dialog.Description />
                <div className="max-w-[90vw] relative">
                  <FilmImageEditor filmId={film?.databaseId!} onChange={(media) => setGallery(media.gallery)} />
                  <div className="absolute right-0 top-0">
                    <AssetManagerTaskLabel />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Dialog.Close>
                    <Button variant="primary">Done</Button>
                  </Dialog.Close>
                </div>
              </Dialog>
            </div>
          </div>
        </InspectorControls>
      )}
      <div
        ref={ref}
        className="theme-dark bg-bg text-fg mb-6 md:md-8 overflow-hidden relative"
        data-mixpanel-area="Film Header"
      >
        <DrawerSnap />
        {showBreadcrumbs && <FilmBreadcrumbs />}
        <div className={cn("w-grid mx-auto pt-6 pb-5", showBreadcrumbs ? "!pt-0" : "")}>
          {/* Film title */}
          <h1 className="type-title-xl lg:type-title-xxl md:w-cols-6 text-balance">
            {!!filmTag && <span className="block">{filmTag}: </span>}
            {filmTitle}
          </h1>
          <div className="flex flex-col md:flex-row justify-between md:items-end mt-4 md:mt-0 gap-4">
            {/* Metadata tags */}
            <div className="flex flex-wrap gap-2">{meta}</div>

            {/* Primary actions (Desktop) */}
            <div className={cx("hidden md:flex gap-3 flex-wrap flex-row")}>{actions}</div>
            {/* Primary actions (Mobile>) */}
            <div className={cx("flex md:hidden -mx-grid-margin mt-3 mb-1")}>
              <FadeScroller fadeSize="lg">
                <div className="flex gap-3 flex-row-reverse px-grid-gutter w-min">{actions}</div>
              </FadeScroller>
            </div>
          </div>
        </div>

        {/* Film header image/gallery */}
        <FilmGallery images={gallery} />
      </div>
    </>
  )
})

type MetaItem = {
  label: string
  showDefault?: boolean
  get: (props: BlockProps["film/film-header"]["film"]) => ReactNode
}

const META_TYPES: MetaItem[] = [
  {
    label: "Director",
    showDefault: true,
    get: (film) =>
      !!film?.credits?.director && film.credits.director.length > 0
        ? `Directed by ${film?.credits?.director.join(", ")}`
        : null,
  },
  {
    label: "Country",
    showDefault: true,
    get: (film) => {
      const primary = film?.filmInfo?.primaryCountry
      const countries = film?.filmInfo?.country
      if (primary) {
        return primary
      } else if (countries && countries.length > 1) {
        const value = countries.join(", ")
        if (value.length < 100) {
          return value
        }
      }
    },
  },
  { label: "Year", showDefault: true, get: (film) => film?.filmInfo?.year },
  {
    label: "Runtime",
    showDefault: true,
    get: (film) => (film?.eventInfo?.runtime ? film?.eventInfo?.runtime + " mins" : null),
  },
  {
    label: "Classification",
    showDefault: true,
    get: (film) => film?.eventInfo?.classification || null,
  },
]

type FilmGalleryProps = {
  images?: { srcSet: string | null; aspect: number | null }[]
}

function FilmGallery(props: FilmGalleryProps) {
  if (props.images?.length === 0) return null

  if (props.images?.length === 1) {
    return (
      <div className="grid-auto pb-3">
        <div className="col-span-12">
          <ResponsiveImage srcSet={props.images[0].srcSet!} className="w-full" />
        </div>
      </div>
    )
  }

  const SliderContent = (
    <div className="w-grid-outer xxl:w-full mx-auto h-[50vw] sm:h-[40vw] md:h-[28vw] max-h-[540px] relative pb-3 md:pb-2">
      <Slider
        className="h-full"
        scrollerClass="flex h-full !scroll-mask-0 scroll-p-grid-margin-full xxl:!px-grid-margin-full xxl:!scroll-p-grid-margin-full gap-x-3 md:gap-x-2"
        arrows
        gridInsert={false}
      >
        {props.images?.map((image, i) => (
          <div key={i} className="aspect-[16_/_9] h-full flex-none">
            <ResponsiveImage srcSet={image.srcSet!} className="!h-full w-auto" />
          </div>
        ))}
      </Slider>
    </div>
  )

  if (props.images?.length === 2) {
    // fit images next to each other on desktop
    // and show slider on mobile
    return (
      <>
        <div className="pb-3 gap-3 hidden md:flex">
          {props.images?.map((image, i) => (
            <div key={i} className="flex-1">
              <ResponsiveImage srcSet={image.srcSet!} className="h-full w-auto aspect-[auto]" />
            </div>
          ))}
        </div>
        <div className="md:hidden">{SliderContent}</div>
      </>
    )
  }

  return SliderContent
}

/**
 * When the user has come from a program page, show a back button.
 * Otherwise, show a 'View the fill program' button
 */
function FilmBreadcrumbs(props: { isInDrawer?: boolean }) {
  const backButton = useProgramBackButton().getBackButton()
  return <Breadcrumbs breadcrumbs={[{ back: true, title: backButton.label, uri: backButton.url, active: false }]} />
}

function DrawerSnap() {
  const drawer = useParentDrawer()
  const [enabled, setEnabled] = useState(true)

  useEffect(() => {
    let disableTimer = null as any
    const unsub = drawer.subscribeToSnap((snap) => {
      if (snap === 1) {
        disableTimer = setTimeout(() => {
          setEnabled(false)
        }, 1000)
      }
    })
    return () => {
      clearTimeout(disableTimer)
      unsub()
    }
  }, [drawer, enabled])

  if (!enabled) return null
  return <Drawer.SnapContainer className="absolute inset-0 -bottom-[60px] pointer-events-none" />
}
